import {
  TrackingElementContext,
  TrackingPromotion,
  TrackingPromotionCreative,
  TrackingPromotionDestination,
  TrackingSectionContext,
} from '../../../types';
import {
  TrackingClientState,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {
  SourceHistoryEventData,
  addContainerToHistory,
  getSourceHistoryEventData,
} from '../helpers/sourceHistory';
import {stringifyEventData} from '../helpers/stringifyEventData';
import {trackRecoList} from './trackRecoList';

interface PromotionDataGA3 extends SourceHistoryEventData {
  id: string | number;
  name: string;
  creative: TrackingPromotionCreative;

  position: number;
  sectionIndexOnPage: number;
  rowIndexInSection: number;
  columnIndexInSection: number;

  initiallyVisibleColumns: number;

  destination: string;
  destinationUrl: string | null;
}

interface PromotionDataGA4 {
  promotion_id: string | number;
  promotion_name: string;

  creative_name: TrackingPromotionCreative | string;
  creative_slot: string;
  index: number;
  destination: string;
  destination_url: string | null;
  linktarget_id: string;
  linktarget: string | null;
  source: string | null;
  location_id: string | null;
  position_bi: string;
}

type PromotionData = {
  GA3: PromotionDataGA3;
  GA4: PromotionDataGA4;
};

export interface PromotionImpressionEvent {
  GA3: {
    event: 'promotionImpression';
    ecommerce: {
      promoView: {
        promotions: PromotionData['GA3'][];
      };
    };
  };
  GA4: {
    event: 'view_promotion';
    content_name: string;
    page_type: string;
    page_type_id: string;
    ecommerce: {
      items: PromotionData['GA4'][];
    };
  };
}

export interface PromotionClickEvent {
  GA3: {
    event: 'promotionClick';
    ecommerce: {
      promoClick: {
        promotions: PromotionData['GA3'][];
      };
    };
  };
  GA4: {
    event: 'select_promotion';
    content_name: string;
    page_type: string;
    page_type_id: string;
    ecommerce: {
      items: PromotionData['GA4'][];
    };
  };
}

export type PromotionEvent = {
  GA3: PromotionImpressionEvent['GA3'] | PromotionClickEvent['GA3'];
  GA4: PromotionImpressionEvent['GA4'] | PromotionClickEvent['GA4'];
};

const dataLayerDestinationDescription = {
  'no-destination': 'no_link',
  external: 'external_link',
  video: 'video',
  liveshopping: 'liveshopping',
};

const getDataLayerDestinationUrl = ({
  destination,
}: {
  destination: TrackingPromotionDestination;
}): string | null => {
  switch (destination.type) {
    case 'internal':
      return destination.page.path;
    case 'from-endpoint':
    case 'liveshopping':
    case 'external':
      return destination.url;
    case 'video':
      return 'video';
    case 'no-destination':
      return null;
  }
};

export const trackPromotionInteraction =
  ({
    type,
    section,
    element,
    promotion,
    pageType,
  }: {
    type: 'impression' | 'click';
    section: TrackingSectionContext;
    element: TrackingElementContext;
    promotion: TrackingPromotion;
    pageType?: string;
  }) =>
  (clientState: TrackingClientStateHolder): PromotionEvent => {
    const currentState = clientState.getCurrent();
    const {currentPage} = currentState;

    if (section.type === 'reco') {
      if (type === 'impression') {
        trackRecoList(clientState)({section});
      }
    }

    if (type === 'click') {
      addContainerToHistory({section, clientState});
    }

    const {promotionsGA4, promotionsGA3} = getPromotions({
      currentState,
      section,
      element,
      promotion,
    });

    let GA3Promotion:
      | PromotionImpressionEvent['GA3']
      | PromotionClickEvent['GA3'];

    let GA4Promotion:
      | PromotionImpressionEvent['GA4']
      | PromotionClickEvent['GA4'];

    const pageTypeId = (() => {
      switch (promotion.creative) {
        case TrackingPromotionCreative.Highlight:
          return `${promotion.highlightId}`;
        case TrackingPromotionCreative.Brand:
          return `${promotion.id}`;
        default:
          return currentPage?.type === 'detailed'
            ? `${currentPage.page?.id || ''}`
            : '';
      }
    })();

    if (type === 'impression') {
      GA3Promotion = {
        event: 'promotionImpression',
        ecommerce: {
          promoView: {
            promotions: promotionsGA3,
          },
        },
      } as PromotionImpressionEvent['GA3'];
      GA4Promotion = {
        event: 'view_promotion',
        content_name: currentPage?.page?.path || '',
        page_type: pageType || currentPage?.page?.type || '',
        page_type_id: pageTypeId,
        ecommerce: {
          items: promotionsGA4,
        },
      } as PromotionImpressionEvent['GA4'];
    } else {
      GA3Promotion = {
        event: 'promotionClick',
        ecommerce: {
          promoClick: {
            promotions: promotionsGA3,
          },
        },
      } as PromotionClickEvent['GA3'];
      GA4Promotion = {
        event: 'select_promotion',
        content_name: currentPage?.page?.path || '',
        page_type: pageType || currentPage?.page?.type || '',
        page_type_id: pageTypeId,
        ecommerce: {
          items: promotionsGA4,
        },
      } as PromotionClickEvent['GA4'];
    }

    const event = {
      GA3: GA3Promotion,
      GA4: GA4Promotion,
    };

    return event;
  };

const getPromotions = ({
  currentState,
  section,
  element,
  promotion,
}: {
  currentState: TrackingClientState;
  section: TrackingSectionContext;
  element: TrackingElementContext;
  promotion: TrackingPromotion;
}) => {
  const sourceHistory = getSourceHistoryEventData({
    state: currentState,
    currentSourceSection: section,
  });

  const destination = (() => {
    if (promotion.destination.type === 'from-endpoint') {
      return promotion.destination.destination;
    }

    return stringifyEventData([
      promotion.destination.type === 'internal'
        ? promotion.destination.page.type
        : dataLayerDestinationDescription[promotion.destination.type],
      promotion.destination.type === 'internal'
        ? promotion.destination.page.id
        : '',
    ]);
  })();

  const destinationUrl = getDataLayerDestinationUrl({
    destination: promotion.destination,
  });

  const promotionsGA3: PromotionDataGA3[] = [
    {
      id: promotion.id,
      name: promotion.name,
      creative: promotion.creative,

      position: element.elementIndexInSection + 1,
      sectionIndexOnPage: section.sectionIndexOnPage + 1,
      rowIndexInSection: element.rowIndexInSection + 1,
      columnIndexInSection: element.columnIndexInSection + 1,

      initiallyVisibleColumns:
        section.type === 'reco' ? section.initiallyVisibleColumns : 1,

      ...sourceHistory,

      destination,
      destinationUrl,
    },
  ];

  let placement = sourceHistory.source?.split('|')[3] ?? '';
  if (promotion.locationId) {
    placement = promotion.locationId;
  }

  let updatedSource = sourceHistory.source;
  if (
    promotion.creative === TrackingPromotionCreative.Highlight &&
    sourceHistory.source
  ) {
    const splitSource = sourceHistory.source.split('|');
    splitSource[0] = promotion.creative;
    splitSource[1] = promotion.highlightId?.toString() || '';
    updatedSource = splitSource.join('|');
  }

  const promotionsGA4: PromotionDataGA4[] = [
    {
      promotion_id: promotion.id,
      promotion_name: promotion.name,
      creative_name: promotion.creativeName || promotion.creative,
      creative_slot:
        promotion.creativeSlot !== undefined
          ? promotion.creativeSlot
          : promotion.creative,
      index: element.elementIndexInSection + 1,
      destination,
      destination_url: destinationUrl,
      linktarget_id: destination,
      linktarget: destinationUrl,
      source: updatedSource,
      location_id: placement,
      position_bi: stringifyEventData([
        section.sectionIndexOnPage + 1 || 0,
        element.rowIndexInSection + 1 || 0,
        element.columnIndexInSection + 1 || 0,
        section.type === 'reco' ? section.initiallyVisibleColumns : 1,
      ]),
    },
  ];

  return {promotionsGA3, promotionsGA4};
};
